import React from 'react';
import { RouteComponentProps, Link, Redirect } from 'react-router-dom';
import AppServices from 'services/AppServices';
import Alert from 'react-bootstrap/Alert';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Badge from 'react-bootstrap/Badge';
import DropdownButton from 'react-bootstrap/DropdownButton';
import Dropdown from 'react-bootstrap/Dropdown';
import Modal from 'react-bootstrap/Modal';
import Layout from 'components/common/Layout';
import Locale, { DisplayLanguages, LanguageCode } from 'localization/Localization';
import { getCurrentLanguage } from 'components/contexts/LocalizationContext';
import { UserConsumer, UserProvider } from 'components/contexts/UserContext';

interface AccountPageState {
	verificationSent?: boolean;
	emailFormValidated?: boolean;
	passwordFormValidated?: boolean;
	modal?: JSX.Element;
	allowEmailSubmit?: boolean;
	busy?: boolean;
	redirect?: string;
}

export class AccountPage extends React.Component<RouteComponentProps<{}>, AccountPageState>
{
	constructor(props: RouteComponentProps<{}>) {
		super(props);
		this.state = {}
		this.openModal = this.openModal.bind(this);
		this.closeModal = this.closeModal.bind(this);
	}

	public componentDidMount() {
		document.title = Locale.getPageTitle('page_account');
	}

	public render() {
		const pageContent = <>
			<h1>{Locale.get('page_account')}</h1>
			<UserConsumer>{user =>
				<>
					<div id="account-settings">
						<Card id='account-section'>
							<Card.Header>
								<Card.Title>{Locale.get('account_header_info')}</Card.Title>
							</Card.Header>
							<Card.Body>
								<p><b>{Locale.get('account_label_id')}</b> {user.id || Locale.get('account_label_not_available')}</p>
								<p>
									<b>{Locale.get('account_label_type')}</b>
									{
										user.org === undefined
											? <Badge variant='success'>{Locale.get('account_type_customer')}</Badge>
											: <Badge variant='primary'>{Locale.get('account_type_pro')}</Badge>
									}
								</p>
							</Card.Body>
							<Card.Footer />
						</Card>
						{
							user.org !== undefined &&
							user.balance !== undefined &&
							<Card id='organization-section'>
								<Card.Header>
									<Card.Title>{Locale.get('account_header_organization')}<Badge variant='primary'>{Locale.get('account_type_pro')}</Badge></Card.Title>
								</Card.Header>
								<Card.Body>
									<p><b>{Locale.get('account_label_org_name')}</b> {user.org || Locale.get('account_label_not_available')}</p>
									<p><b>{Locale.get('account_label_org_balance')}</b> {user.balance.available.toString()} {Locale.get('account_label_credits')}</p>
								</Card.Body>
								<Card.Footer />
							</Card>
						}
						<Form onSubmit={this.submitEmail.bind(this)} validated={this.state.emailFormValidated} noValidate>
							<Card id='email-section'>
								<Card.Header>
									<Card.Title>
										{Locale.get('account_header_email')}
										{
											user.email === undefined &&
											<Badge variant='dark'>{Locale.get('account_email_status_not_set')}</Badge>
										}
										{
											user.email &&
											user.emailVerified !== true &&
											<Badge variant='warning'>{Locale.get('account_email_status_unverified')}</Badge>
										}
										{
											user.email &&
											user.emailVerified &&
											<Badge variant='success'>{Locale.get('account_email_status_confirmed')}</Badge>
										}
									</Card.Title>
								</Card.Header>
								{user.email &&
									<Card.Body>
										<Form.Group controlId='email'>
											{/* {
											user.email === undefined &&
											<Alert variant='warning'>{Locale.get('home_alertEmailNotDefined')}</Alert>
										} */}
											{
												user.email !== undefined &&
												user.emailVerified !== true &&
												this.state.emailFormValidated !== true &&
												this.state.verificationSent === undefined &&
												<Alert variant='warning'>
													{Locale.get('home_alertEmailNotVerified')}
													<br /><Link to='' onClick={this.onResendVerification.bind(this)}>{Locale.get('home_linkResendVerification')}</Link>
												</Alert>
											}
											{
												this.state.verificationSent &&
												<Alert variant='success'>{Locale.get('home_alertVerificationSent')}</Alert>
											}
											<Form.Control
												type='email'
												required
												defaultValue={user.email}
												disabled={true} //user.emailVerified}
												onChange={this.onEmailChanged.bind(this)}
											/>
											<Form.Control.Feedback type='invalid'>{Locale.get('account_feedback_check_email')}</Form.Control.Feedback>
											<Form.Text>{Locale.get('account_email_notice')}</Form.Text>
										</Form.Group>
									</Card.Body>
								}
								<Card.Footer>
									{
										user.emailVerified !== true &&
										<Button id='apply-email' type='submit'
											disabled={this.state.busy || this.state.allowEmailSubmit !== true}>
											{Locale.get('button_save')}
										</Button>
									}
								</Card.Footer>
							</Card>
						</Form>
						<Card id='language-section'>
							<Card.Header>
								<Card.Title>{Locale.get('account_header_language')}</Card.Title>
							</Card.Header>
							<Card.Body>
								<div>
									<DropdownButton
										id='language-menu'
										size='lg'
										disabled={this.state.busy}
										title={DisplayLanguages[getCurrentLanguage()]}>
										{
											Locale.getAvailableLanguageCodes()
												.map(l => {
													return <Dropdown.Item key={l} onClick={() => this.submitLanguage(l)}>
														{DisplayLanguages[l]}
													</Dropdown.Item>
												})
										}
									</DropdownButton>
								</div>
							</Card.Body>
							<Card.Footer />
						</Card>
						<Form onSubmit={this.submitPassword.bind(this)} validated={this.state.passwordFormValidated} noValidate>
							<Card id='password-section'>
								<Card.Header>
									<Card.Title>{Locale.get('account_header_password')}</Card.Title>
								</Card.Header>
								<Card.Body>
									<div>
										{
											user.hasPassword !== true &&
											<Alert variant='warning'>{Locale.get('home_alertPasswordNotSet')}</Alert>
										}
									</div>
									{
										user.hasPassword &&
										<Form.Group controlId='oldPassword'>
											<Form.Label>{Locale.get('account_label_password_old')}</Form.Label>
											<Form.Control
												type='password'
												required={user.hasPassword} />

											<Form.Control.Feedback type='invalid'>{Locale.get('account_feedback_enter_old_password')}</Form.Control.Feedback>
										</Form.Group>
									}
									<Form.Group controlId='newPassword'>
										<Form.Label>{Locale.get('account_label_password_new')}</Form.Label>
										<Form.Control type='password' required />
										<Form.Control.Feedback type='invalid'>{Locale.get('account_feedback_enter_new_password')}</Form.Control.Feedback>
									</Form.Group>
									<Form.Group controlId='confirmPassword'>
										<Form.Label>{Locale.get('account_label_password_confirm')}</Form.Label>
										<Form.Control type='password' required onChange={this.confirmPasswordCheck} />
										<Form.Control.Feedback type='invalid'>{Locale.get('account_feedback_password_mismatch')}</Form.Control.Feedback>
									</Form.Group>
								</Card.Body>
								<Card.Footer>
									<Button id='apply-password' type='submit' disabled={this.state.busy}>{Locale.get('button_save')}</Button>
								</Card.Footer>
							</Card>
						</Form>
						<Card id='delete-account-section'>
							<Card.Header>
								<Card.Title>{Locale.get('account_header_delete')}</Card.Title>
							</Card.Header>
							<Card.Body>
								<p>{Locale.get('account_delete_notice')}</p>
								<div>
									<Button id='delete-account' size='lg'
										onClick={this.deleteAccount.bind(this)}
										disabled={this.state.busy}>
										{Locale.get('account_button_delete')}
									</Button>
								</div>
							</Card.Body>
							<Card.Footer />
						</Card>
					</div>

				</>
			}</UserConsumer>
			{this.state.modal}
		</>

		return <Layout>
			{this.state.redirect && <Redirect to={this.state.redirect} />}
			{pageContent}
		</Layout>;
	}

	private submitEmail(e: React.FormEvent<HTMLFormElement>) {
		e.preventDefault();
		const form = e.currentTarget;
		const email = form.email.value as string;

		if (form.checkValidity()) {
			this.setState({ modal: undefined, busy: true });

			AppServices.userService.modifyUser({ email: email.trim() })
				.then(result => {
					if (result === true) {
						UserProvider.refresh()
							.then(() => this.openModal(
								Locale.get('label_success'),
								<p>{Locale.get('account_message_email_changed')}</p>,
								'autoClose'))
					}
					else {
						this.openModal(
							Locale.get('label_error'),
							<p>{Locale.get('account_error_email_not_changed')}</p>,
							'closeButton');
					}

					this.setState({ busy: false })
				})
		}

		this.setState({ emailFormValidated: true });
	}

	private submitPassword(e: React.FormEvent<HTMLFormElement>) {
		e.preventDefault();
		const form = e.currentTarget;

		const oldpw = form.oldPassword.value;
		const newpw = form.newPassword.value;

		if (form.checkValidity()) {
			this.setState({ modal: undefined, busy: true });

			AppServices.userService.modifyUser({ oldPassword: oldpw, newPassword: newpw })
				.then(result => {
					if (result === true) {
						UserProvider.refresh()
							.then(() => this.openModal(
								Locale.get('label_success'),
								<p>{Locale.get('account_message_password_changed')}</p>,
								'autoClose'))
					}
					else {
						this.openModal(
							Locale.get('label_error'),
							<p>{Locale.get('account_error_password_not_changed')}</p>,
							'closeButton');
					}

					this.setState({ busy: false })
				})
		}

		this.setState({ passwordFormValidated: true });
	}

	private submitLanguage(lang: LanguageCode) {
		this.setState({ modal: undefined, busy: true });

		AppServices.userService.modifyUser({ language: lang })
			.then(result => {
				if (result === true) {
					UserProvider.refresh()
						.then(result => this.openModal(
							Locale.get('label_success'),
							<p>{Locale.get('account_message_language_changed')}</p>,
							'autoClose'))
				}
				else {
					this.openModal(
						Locale.get('label_error'),
						<p>{Locale.get('account_error_language_not_changed')}</p>,
						'closeButton');
				}

				this.setState({ busy: false })
			})
	}

	private deleteAccount() {
		const doDelete = () => {
			this.setState({ busy: true })
			AppServices.userService.deleteUser()
				.then(result => {
					if (result === true) {
						this.setState({ redirect: '/login' })
					}
					else {
						this.openModal(
							Locale.get('label_error'),
							<p>{Locale.get('account_error_delete_failed')}</p>, 'closeButton');
						this.setState({ busy: false })
					}
				})
		}

		this.openModal(
			Locale.get('label_confirm'),
			<p>{Locale.get('account_confirm_delete')}</p>,
			'closeButton',
			<>
				<Button variant='danger' onClick={doDelete}>{Locale.get('account_button_confirm_delete')}</Button>
				<Button variant='primary' onClick={this.closeModal}>{Locale.get('button_cancel')}</Button>
			</>
		);
	}

	private onEmailChanged(e: React.ChangeEvent<HTMLInputElement>) {
		const email = e.currentTarget.value;
		const form = e.currentTarget.form;
		const user = UserProvider.getData();

		const changed = user.email !== email;
		const valid = form !== null ? form.checkValidity() : true;

		this.setState({ allowEmailSubmit: valid && changed, emailFormValidated: changed })
	}

	private confirmPasswordCheck(e: React.ChangeEvent<HTMLInputElement>) {
		const form = e.currentTarget.form;
		if (form !== null) {
			const confirmPw = e.currentTarget as HTMLInputElement;
			const newPw = form.newPassword as HTMLInputElement;
			let match = confirmPw.value === newPw.value && confirmPw.value.length > 0;
			confirmPw.setCustomValidity(match ? '' : 'Does not match');
		}
	}

	private openModal(title: string, body: JSX.Element, type: 'closeButton' | 'autoClose', footer?: JSX.Element) {
		this.setState({
			modal: <>
				<Modal centered onHide={this.closeModal.bind(this)} show>
					<Modal.Header closeButton={type === 'closeButton'}>
						<Modal.Title>{title}</Modal.Title>
					</Modal.Header>
					<Modal.Body>
						{body}
					</Modal.Body>
					{
						footer !== undefined &&
						<Modal.Footer>{footer}</Modal.Footer>
					}
				</Modal>
			</>
		})

		if (type === 'autoClose') {
			setTimeout(this.closeModal.bind(this), 2000);
		}
	}

	private closeModal() {
		this.setState({ modal: undefined })
	}

	private onResendVerification(e: React.MouseEvent<HTMLAnchorElement>) {
		e.preventDefault();

		this.setState({ modal: undefined, verificationSent: false, busy: true })
		const email = UserProvider.getData().email;

		if (email === undefined)
			return;

		AppServices.loginService.resendVerification(email)
			.then(() => {
				this.setState({ verificationSent: true })
			})
			.catch(reason => {
				console.error(reason);
				this.setState({ verificationSent: false })
				this.openModal(
					Locale.get('label_error'),
					<p>{Locale.get('account_error_verification_mail_not_sent')}</p>,
					'closeButton')
			})
	}
}